import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../content/ContentContext";
import { setPageMeta } from "../lib/seo";
import { SectionLabel, ButtonLink } from "../components/Ui";
import { Reveal } from "../components/Reveal";
import { CaseVisual } from "../dashboards/CaseVisual";
import { usePalette } from "../dashboards/kit";
import EmailMeButton from "../components/EmailMeButton";

function CaseIntro({ cs, index, services, navy, teal }) {
  const relatedService = services.find((s) => s.id === cs.relatedService);
  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
      <div className="lg:col-span-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid={`cs-label-${cs.id}`}>
          <span className="font-semibold" style={{ color: navy }}>Case {String(index + 1).padStart(2, "0")}</span>
          <span className="mx-2" aria-hidden="true">·</span>{cs.topic || cs.area}
        </p>
        <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl" data-testid={`cs-title-${cs.id}`}>
          {cs.title}
        </h2>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted" data-testid={`cs-problem-${cs.id}`}>{cs.problem}</p>
        {cs.tools?.length > 0 && (
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
            Tools: <span className="text-ink">{cs.tools.join(" · ")}</span>
          </p>
        )}
      </div>
      <div className="lg:col-span-6">
        <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: navy }}>What I did</h3>
        <ul className="mt-5 space-y-3" data-testid={`cs-did-${cs.id}`}>
          {(cs.whatIDid || []).map((item, i) => (
            <li key={item} className="flex gap-4 text-base leading-relaxed text-ink">
              <span className="mt-0.5 font-mono text-xs font-semibold" style={{ color: navy }}>0{i + 1}</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {cs.result && (
          <div className="mt-8 border-l-4 pl-5" style={{ borderColor: teal }} data-testid={`cs-result-${cs.id}`}>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: teal }}>Result</p>
            <p className="mt-2 font-serif text-xl font-medium leading-snug text-ink sm:text-2xl">{cs.result}</p>
          </div>
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/case-studies/${cs.id}`} data-testid={`cs-detail-link-${cs.id}`} className="text-sm font-semibold text-plum underline-offset-4 hover:text-accent hover:underline">
            Full write-up →
          </Link>
          {relatedService && (
            <Link to={`/?service=${encodeURIComponent(relatedService.name)}#contact`} data-testid={`cs-discuss-${cs.id}`} className="text-sm font-semibold text-plum underline-offset-4 hover:text-accent hover:underline">
              Discuss {relatedService.name.toLowerCase()} →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CaseStudies() {
  const { content } = useContent();
  const { site, caseStudies, dashboards, process, services } = content;
  const c = usePalette();
  const visible = caseStudies.filter((cs) => !cs.hidden);

  useEffect(() => {
    setPageMeta(
      "Case Studies — Savvy Stephanie Analytics & Consulting",
      "Interactive case studies from Stephanie Madojemu's BI and business analysis work: supplier invoice reconciliation, customer returns, supply chain reporting, automation and process mapping.",
      { path: "/case-studies" }
    );
  }, []);

  return (
    <main className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
      <Reveal>
        <SectionLabel>Case Studies</SectionLabel>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="cases-heading">
          {site.caseStudiesHeading}
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">{site.caseStudiesIntro}</p>
        <p className="mt-4 max-w-2xl font-mono text-[11px] uppercase tracking-[0.14em] text-muted" data-testid="cases-note">
          {site.caseStudiesNote}
        </p>
        <nav aria-label="Case studies on this page" className="mt-8 flex flex-wrap gap-2" data-testid="cases-jump-nav">
          {visible.map((cs, i) => (
            <a key={cs.id} href={`#${cs.id}`} data-testid={`case-jump-${cs.id}`}
              className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-plum hover:text-plum">
              {String(i + 1).padStart(2, "0")} · {cs.topic || cs.area}
            </a>
          ))}
        </nav>
      </Reveal>

      {visible.map((cs, i) => (
        <section key={cs.id} id={cs.id} className="mt-24 scroll-mt-24 border-t border-line pt-16" data-testid={`cs-section-${cs.id}`}>
          <Reveal>
            <CaseIntro cs={cs} index={i} services={services} navy={c.navy} teal={c.teal} />
          </Reveal>
          <Reveal className="mt-12">
            <CaseVisual cs={cs} dashboards={dashboards} process={process} />
          </Reveal>
        </section>
      ))}

      <Reveal className="mt-24">
        <div className="rounded-2xl bg-plum p-10 text-center">
          <h2 className="font-serif text-2xl font-medium text-cream sm:text-3xl">Want reporting like this for your business?</h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-cream/80">
            Tell me what you need — you&rsquo;ll get a clear scope and a fixed price before anything begins.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <ButtonLink to="/#contact" testid="case-studies-cta" className="bg-cream !text-plum hover:bg-white">Start a project</ButtonLink>
            <EmailMeButton variant="cream" testid="case-studies-email-me-button" />
          </div>
        </div>
      </Reveal>
    </main>
  );
}
